import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { DeviceMobile, GoogleLogo, Globe, Key, Lock, ShieldCheck } from '@phosphor-icons/react';
import { ChangePasswordDialog } from '../../../../../components/settings/change-password-dialog';
import { SettingCard, SettingRow } from '../../../../../components/settings/setting-card';
import { useAccountSecurity } from '../../../../../components/settings/use-account-security';
import { useStaffReauth } from '../../../../../components/settings/use-staff-reauth';
import { useTwoFactorControls } from '../../../../../components/settings/use-two-factor-controls';
import { SignOutOthersDialog } from '../../../../../components/settings/two-factor-dialogs';
import { Banner } from '../../../../../components/ui/banner';
import { Button } from '../../../../../components/ui/button';
import { Skeleton } from '../../../../../components/ui/skeleton';
import { StatusTag } from '../../../../../components/ui/status-tag';
import { describeUserAgent, formatActivity, formatChanged } from '../../../../../lib/session-format';
import { sessionQueryOptions } from '../../../../../lib/session';
import { staffSecurityQuery, useStaffSecurity } from '../../../../../lib/workspace-api';
import { ErrorState } from '../../../../../components/ui/state-views';

export const Route = createFileRoute('/subdomain/admin/_staff/settings/security')({
  component: SecurityPage,
});

const GRACE_DATE = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' });

function SecurityPage() {
  const queryClient = useQueryClient();
  const { data: security } = useStaffSecurity();
  const { guard, dialog: reauthDialog } = useStaffReauth();
  const account = useAccountSecurity();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);

  const twoFactorOn = Boolean(account.current?.user.twoFactorEnabled);
  const twoFactor = useTwoFactorControls({
    guard,
    // The workspace requires two-step from people in this role: it can only move to a new phone.
    mandatory: security?.twoFactorMandatory ?? false,
    audience: 'staff',
    onProblem: account.setProblem,
    onChanged: () => {
      void queryClient.invalidateQueries({ queryKey: sessionQueryOptions.queryKey });
      void queryClient.invalidateQueries({ queryKey: staffSecurityQuery.queryKey });
    },
  });

  const { accounts, sessions, credential, google, link, unlink, revoke, otherDevices } = account;
  const graceEnds = security?.graceEndsAt ? GRACE_DATE.format(new Date(security.graceEndsAt)) : undefined;

  return (
    <>
      {security?.twoFactorRequired ? (
        <Banner tone="warning" title="Two-step is required for staff">
          Your admin turned this on.{graceEnds ? ` Set it up by ${graceEnds}, after that` : ' After the grace period'}{' '}
          we’ll ask you to do it when you sign in.
        </Banner>
      ) : null}
      {account.problem ? <Banner tone="error">{account.problem}</Banner> : null}

      <SettingCard title="Sign-in methods" subtitle="How you get into your staff account.">
        {accounts.isLoading ? (
          <Skeleton className="my-(--mbm-space-3) h-(--mbm-size-list-row-tall) rounded-(--mbm-radius-md)" />
        ) : (
          <>
            <SettingRow
              icon={Lock}
              title="Email & password"
              subtitle={credential ? `Last changed ${formatChanged(credential.updatedAt)}` : 'Not set'}
              trailing={
                credential ? (
                  <Button variant="secondary" size="row" onClick={() => setPasswordOpen(true)}>
                    Change password
                  </Button>
                ) : undefined
              }
            />
            {security?.allowGoogleSignIn !== false ? (
              <SettingRow
                icon={GoogleLogo}
                title="Google"
                subtitle={google ? `${account.current?.user.email} · connected` : 'Not connected'}
                trailing={
                  google ? (
                    <Button
                      variant="secondary"
                      size="row"
                      loading={unlink.isPending}
                      onClick={() => guard('disconnect Google', () => unlink.mutate(google.id))}
                    >
                      Disconnect
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      size="row"
                      loading={link.isPending}
                      onClick={() => link.mutate('google')}
                    >
                      Connect
                    </Button>
                  )
                }
              />
            ) : null}
          </>
        )}
      </SettingCard>

      <SettingCard
        title="Two-step verification"
        subtitle="A 6-digit code from your phone when you sign in on a new browser."
      >
        <SettingRow
          icon={ShieldCheck}
          title="Authenticator app"
          subtitle={twoFactorOn ? 'Codes from the app on your phone' : 'Not set up yet'}
          trailing={<StatusTag label={twoFactorOn ? 'On' : 'Off'} tone={twoFactorOn ? 'green' : 'grey'} dot />}
        />
        <SettingRow
          icon={Key}
          muted={!twoFactorOn}
          title="Backup codes"
          subtitle={twoFactorOn ? 'Use one if you lose your phone.' : 'You’ll get 10 codes when you turn on two-step.'}
          trailing={
            twoFactorOn ? (
              <Button variant="secondary" size="row" onClick={twoFactor.viewCodes}>
                View codes
              </Button>
            ) : (
              <Button variant="secondary" size="row" disabled>
                Not available
              </Button>
            )
          }
        />
        <SettingRow
          icon={twoFactorOn ? Lock : ShieldCheck}
          title={twoFactorOn ? 'Turn off two-step verification' : 'Turn on two-step verification'}
          subtitle={
            twoFactorOn
              ? 'Not recommended. You can turn it back on any time.'
              : 'Takes about a minute. You’ll need your phone.'
          }
          trailing={
            <Button
              variant="secondary"
              size="row"
              onClick={twoFactorOn ? twoFactor.requestTurnOff : twoFactor.startSetup}
            >
              {twoFactorOn ? 'Turn off' : 'Turn on'}
            </Button>
          }
        />
      </SettingCard>

      <SettingCard
        title="Where you’re signed in"
        subtitle="Sign out anything you don’t recognise."
        action={
          otherDevices.length > 0 ? (
            <button
              type="button"
              onClick={() => setSignOutOpen(true)}
              className="rounded-(--mbm-radius-xs) text-label-medium text-brand-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
            >
              Sign out of all other devices
            </button>
          ) : undefined
        }
      >
        {sessions.isLoading ? (
          <Skeleton className="my-(--mbm-space-3) h-(--mbm-size-list-row-tall) rounded-(--mbm-radius-md)" />
        ) : sessions.isError ? (
          <ErrorState
            title="Couldn’t load your devices"
            className="py-(--mbm-space-5)"
            onRetry={() => void sessions.refetch()}
          />
        ) : (
          sessions.data?.map((s) => {
            const isThisDevice = s.token === account.current?.session.token;
            const mobile = /iphone|android|mobile|ipad/i.test(s.userAgent ?? '');
            return (
              <SettingRow
                key={s.id}
                icon={mobile ? DeviceMobile : Globe}
                title={describeUserAgent(s.userAgent)}
                subtitle={[s.ipAddress, formatActivity(s.updatedAt)].filter(Boolean).join(' · ')}
                trailing={
                  isThisDevice ? (
                    <StatusTag label="This device" tone="green" dot />
                  ) : (
                    <Button
                      variant="secondary"
                      size="row"
                      loading={revoke.isPending && revoke.variables === s.token}
                      onClick={() => revoke.mutate(s.token)}
                    >
                      Sign out
                    </Button>
                  )
                }
              />
            );
          })
        )}
      </SettingCard>

      <p className="rounded-(--mbm-radius-md) bg-feedback-info-bg px-(--mbm-space-4) py-(--mbm-space-3) text-body-small text-text-primary">
        For your safety, we ask for your password again before sensitive actions like viewing backup codes or turning
        off two-step. It lasts {security?.reauthMinutes ?? 10} minutes.
      </p>

      {reauthDialog}
      {twoFactor.dialogs}
      <ChangePasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
      <SignOutOthersDialog
        open={signOutOpen}
        deviceNames={otherDevices.map((s) => describeUserAgent(s.userAgent))}
        busy={account.revokeOthers.isPending}
        onClose={() => setSignOutOpen(false)}
        onConfirm={() => account.revokeOthers.mutate(undefined, { onSettled: () => setSignOutOpen(false) })}
      />
    </>
  );
}
